import { useState, useEffect } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Spinner } from "@/components/Spinner";
import { recall, remember } from "@/lib/lastKnown";
import { InflowLogo } from "@/components/InflowLogo";
import { WheelHeroCard } from "@/components/WheelHeroCard";
import { Icon } from "@/components/Icon";
import { TITLES } from "@/lib/appMap";
import type { IconName } from "@/components/Icon";
import { LockedSectionPopup } from "@/components/LockedSectionPopup";
import { SupportPopup } from "@/components/SupportPopup";
import { BirthdayGiftBanner } from "@/components/BirthdayGiftModal";
import { RetentionOfferBanner } from "@/components/RetentionOffer";
import { openBirthdayGift } from "@/lib/birthdayGift";
import { SubscriptionCard } from "@/components/SubscriptionCard";
import { CornerBadge } from "@/components/CornerBadge";
import { MCP_PAGE_SEEN_KEY } from "@/pages/McpConnect";
import { BirthdayModal } from "@/components/BirthdayModal";

// Ключ анонсу нових квестів (owner 2026-07-16). Показуємо попап раз, поки не
// переглянув. При додаванні НАСТУПНИХ квестів — бампнути суфікс → попап
// покажеться всім знову.

type LockedSectionKey = "knowledge_base" | "course" | "saved";
import {
  useCourse,
  useMe,
  useMySubscription,
  useReferrals,
  useUpdateProfile,
} from "@/api/hooks";
import { tg } from "@/lib/telegram";
import { showAppAlert } from "@/lib/appAlert";
import { openTelegramChat } from "@/lib/openExternal";
import { isActiveSubscription } from "@/api/types";

/**
 * Inflow Home (REVIEW 2026-05-17):
 *   Header: Logo (left) + Profile pill (right)
 *   Hero «Привіт, ім'я.»
 *   ── Підписка featured DARK card ──
 *   Одна картка з 4 рядками:
 *      Стрічка / Квести і бонуси / Реферали
 *   Блок «Навчання»: Курс + База знань
 *   Адмін панель (admin-only DARK)
 */

interface MenuRow {
  id: string;
  title: string;
  subtitle: string;
  icon: IconName;
  /** Куди веде рядок. Немає — значить це дія (див. `onClick`). */
  href?: string;
  /** Дія замість переходу — потрібна «Підтримці» (owner 2026-09-01). */
  onClick?: () => void;
  meta?: string;
  locked?: boolean;  // показати 🔒 замість стрілки (немає підписки)
  lockedKey?: LockedSectionKey;  // ключ для LockedSectionPopup
}

function formatSubExpires(endsAt: string | null | undefined): string | null {
  if (!endsAt) return null;
  try {
    const d = new Date(endsAt);
    const day = String(d.getDate()).padStart(2, "0");
    const months = ["січ", "лют", "бер", "квіт", "трав", "черв", "лип", "серп", "вер", "жовт", "лист", "груд"];
    return `до ${day} ${months[d.getMonth()]}`;
  } catch {
    return null;
  }
}

export function HomePage() {
  const meQuery = useMe();
  const subQuery = useMySubscription();
  const refsQuery = useReferrals();
  const courseQuery = useCourse();

  const [lockedSection, setLockedSection] = useState<LockedSectionKey | null>(
    null,
  );
  // ⚠️ Це має стояти ДО будь-якого раннього виходу: хук після умовного
  // return ламає порядок хуків (React #310, owner 2026-09-05 — біла сторінка
  // «Щось пішло не так»).
  //
  // Свіжий стан підписки — якщо він уже приїхав. Поки ні, беремо останній
  // відомий із пристрою: так перший кадр збігається з тим, що людина бачила
  // минулого разу, і сторінка не перебудовується на очах.
  const subFresh = subQuery.data ? isActiveSubscription(subQuery.data) : undefined;
  const subTestFresh = subQuery.data
    ? (subQuery.data as { plan?: string }).plan === "test"
    : undefined;
  useEffect(() => {
    if (subFresh === undefined) return;
    remember("paid", subFresh);
    remember("test", !!subTestFresh);
  }, [subFresh, subTestFresh]);

  const [supportOpen, setSupportOpen] = useState(false);
  // Бейдж «new» на «MCP від inFLOW» — поки людина хоч раз не відкрила сторінку
  // (owner 2026-10-30). Памʼять пристрою; немає доступу — бейдж просто видно.
  const [mcpSeen] = useState(() => {
    try {
      return localStorage.getItem(MCP_PAGE_SEEN_KEY) === "1";
    } catch {
      return false;
    }
  });

  // Попап дня народження прямо з розсилки: кнопка веде на головну з ?bday=1
  // (owner 2026-09-06: «чтобы из неё сразу на главное открывался поп-ап»).
  // Параметр саме `bday`, а не `gift`/`birthday`: `gift` уже означає вікно
  // подарунка, і колись зіткнення двох сенсів одного параметра вбило кнопки
  // оферів.
  const [searchParams, setSearchParams] = useSearchParams();
  const [bdayOpen, setBdayOpen] = useState(false);
  const updateProfile = useUpdateProfile();
  useEffect(() => {
    if (searchParams.get("bday") !== "1") return;
    setBdayOpen(true);
    // Прибираємо параметр одразу: інакше закритий попап відкриється знову на
    // наступному рендері з тим самим URL.
    const next = new URLSearchParams(searchParams);
    next.delete("bday");
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);

  // Чекаємо не лише «хто це», а й підписку: без неї головна встигала
  // намалюватись у безплатному вигляді й через мить перескакувала на PRO —
  // те саме моргання, яке власник бачив при кожному відкритті (2026-09-04).
  // ⛔ Не чекаємо тут ні підписку, ні колесо. Спроба дочекатись коштувала до
  // 1.5 с на КОЖНОМУ відкритті — owner 2026-09-05: «апка грузит в несколько
  // раз дольше, чем до апдейта». Від моргання рятує не очікування, а те, що
  // блок підписки поки не знає стану, малює нейтральну заглушку, а не
  // безплатний варіант.
  if (meQuery.isLoading) return <Spinner />;
  if (meQuery.isError) {
    // НІКОЛИ не лишаємо юзера з мертвим екраном (owner 2026-07-08): показуємо
    // зрозуміле повідомлення + кнопки «повторити» / «перезавантажити».
    return (
      <div className="flex flex-col items-center justify-center gap-4 p-8 text-center">
        <div className="text-3xl">😕</div>
        <div className="max-w-[18.75rem] text-[0.875rem] leading-relaxed text-ink-dim [text-wrap:balance]">
          Не вдалося завантажити дані. Перевір інтернет і спробуй ще раз.
        </div>
        <div className="flex gap-3">
          <button
            type="button"
            onClick={() => meQuery.refetch()}
            className="kx-press rounded-[0.875rem] bg-accent px-5 py-2.5 font-display text-[0.875rem] font-bold text-white"
          >
            Спробувати ще раз
          </button>
          <button
            type="button"
            onClick={() => window.location.reload()}
            className="kx-press rounded-[0.875rem] border border-hair px-5 py-2.5 font-display text-[0.875rem] font-bold text-ink"
          >
            Перезавантажити
          </button>
        </div>
      </div>
    );
  }

  const me = meQuery.data!;

  const isPaid = subFresh ?? recall("paid") ?? false;
  const activeSub =
    subQuery.data && isActiveSubscription(subQuery.data) ? subQuery.data : null;
  // Тестова підписка → «Тест», повноцінна → «PRO» (юзер 2026-06-08).
  const isTest = subFresh !== undefined ? activeSub?.plan === "test" : (recall("test") ?? false);
  const subTier = isTest ? "TEST" : "PRO";
  // Преміум-доступ = активна ПОВНА підписка (НЕ тест). На тест-підписці
  // База знань і Курс закриті (юзер
  // 2026-06-11). Джерела вакансій — доступні на тесті (гейтяться по isPaid).
  const isPremium = isPaid && !isTest;
  //: Чи знаємо ми вже стан підписки. Поки ні — краще заглушка, ніж чужий стан.
  // «Знаємо» — це або свіжа відповідь, або памʼять пристрою.
  const subKnown = subFresh !== undefined || recall("paid") !== undefined;
  const subEnds = activeSub ? formatSubExpires(activeSub.ends_at) : null;
  const referralsCount = refsQuery.data?.referrals_count ?? 0;


  // Course progress — курс відкритий весь одразу (юзер 2026-06-14), тому
  // показуємо % ПРОЙДЕНОГО (переглянутих уроків), а не «розблоковано N/7».
  const courseItems = courseQuery.data?.items ?? [];
  const courseTotal = courseQuery.data?.total ?? (courseItems.length || 7);
  const courseViewed =
    courseQuery.data?.viewed_count ??
    courseItems.filter((l) => l.viewed).length;


  // Решта rows (без Курсу — він рендериться окремим компонентом)
  // Замочок (locked) показуємо коли немає платної підписки.
  // KB закрита для FREE — потребує підписку.
  // Owner 2026-09-01: замість одного блоку з трьох рядків — два по два.
  // Зверху те, чим користуються щодня, нижче — заробіток.
  const mainRows: MenuRow[] = [
    {
      id: "feed",
      title: TITLES.feed,
      subtitle: "Свіжі вакансії за останні 7 днів",
      icon: "flame",
      href: "/feed",
    },
    {
      id: "profile",
      title: TITLES.profile,
      subtitle: "Налаштування і підписка",
      icon: "user",
      href: "/profile",
    },
  ];
  // Збережені — розділ бачать УСІ, але без підписки він під замком
  // (owner 2026-09-09: «он должен быть, просто серый под замочком»).
  // Ховати його зовсім було помилкою: людина не бачить, чого позбавлена.
  mainRows.splice(1, 0, {
    id: "saved",
    title: TITLES.saved,
    subtitle: "Те, що ти відклав, щоб повернутись",
    icon: "bookmark",
    href: "/saved",
    locked: !isPaid,
    lockedKey: "saved",
  });

  const earnRows: MenuRow[] = [
    {
      id: "quests",
      title: TITLES.quests,
      subtitle: "Виконуй квести — отримуй бонуси на оплату",
      icon: "star",
      href: "/quests",
    },
    {
      id: "refs",
      title: TITLES.refs,
      subtitle: "Запрошуй друзів і заробляй",
      icon: "users",
      meta: referralsCount > 0 ? String(referralsCount) : undefined,
      href: "/referrals",
    },
  ];

  return (
    <div className="relative min-h-screen overflow-hidden">
      {/* День народження — попап відкривається з розсилки (?bday=1) */}
      {bdayOpen && (
        <BirthdayModal
          onClose={() => setBdayOpen(false)}
          onSave={(isoDate) =>
            updateProfile.mutate(
              { birthday: isoDate },
              {
                onSuccess: () => {
                  setBdayOpen(false);
                  tg()?.HapticFeedback?.notificationOccurred?.("success");
                },
                onError: (e) => showAppAlert((e as Error).message),
              },
            )
          }
          saving={updateProfile.isPending}
        />
      )}

      {/* Warm aura перенесено в Layout.tsx — застосовується глобально */}

      {/* HEADER: логотип + колесо фортуни (owner 2026-09-01). Кнопка
          «Профіль» переїхала в перший блок повноцінним рядком, підтримка —
          у блок із Курсом; у кутку тепер міні-колесо з таймером. */}
      <div className="relative flex items-center justify-between px-5 pt-5">
        <InflowLogo size={18} to="/" />
        {/* Підтримка — овальна таблетка навпроти логотипа (owner 2026-09-01). */}
        <button
          type="button"
          onClick={() => {
            tg()?.HapticFeedback?.impactOccurred?.("light");
            setSupportOpen(true);
          }}
          className="flex h-8 items-center gap-1.5 rounded-full bg-surface-soft px-3 font-display text-[0.75rem] font-semibold text-ink shadow-card-soft active:scale-95 transition"
        >
          <Icon name="headset" size={14} color="#FE6B04" />
          Підтримка
        </button>
      </div>

      {/* Колесо (owner 2026-09-01). У підписника воно ПЕРШЕ — йому нічого
          не треба відкривати, тож головна дія на екрані саме тут. У того,
          хто без підписки, картка йде НИЖЧЕ тёмного банера, щоб не
          перебивати «Відкрий повний доступ». */}
      {subKnown && isPremium && (
        <div className="px-5 pt-4">
          <WheelHeroCard />
        </div>
      )}

      {/* SUBSCRIPTION featured DARK card — conversion-optimized */}
      {(() => {
        const daysLeft = activeSub?.days_left ?? 0;
        const isFrozen = activeSub?.status === "frozen";
        const expiringSoon = isPaid && !isFrozen && daysLeft <= 7;
        // Залишок у днях + годинах (юзер 2026-06-08).
        const _hoursTotal = activeSub
          ? Math.max(0, Math.floor((new Date(activeSub.ends_at).getTime() - Date.now()) / 3_600_000))
          : 0;
        const leftLabel = `${Math.floor(_hoursTotal / 24)} дн.${
          _hoursTotal % 24 > 0 ? ` ${_hoursTotal % 24} год.` : ""
        }`;

        // Активна підписка → НЕ показуємо картку на головній (юзер
        // 2026-06-11: «займає місце, керування є в профілі»). Статус — у
        // eyebrow. Замість картки лишаємо невеликий відступ, щоб меню не
        // тулилось до імені (юзер 2026-06-11).
        if (isPaid) return <div className="h-1" aria-hidden />;
        // Стан підписки ще їде — тримаємо місце, але не показуємо
        // «безплатний» банер: саме він і моргав на PRO (owner 2026-09-04).
        if (!subKnown)
          return (
            <div className="px-5 pt-8" aria-hidden>
              <div className="h-[9.5rem] w-full animate-pulse rounded-ios-hero bg-ink/[0.05]" />
            </div>
          );

        return (
          <div className="px-5 pt-8">
            {/* Оформлення — спільний `SubscriptionCard` (owner 2026-09-10:
                «ці два блоки в нашому стилі апки»). Тут лишається ЛИШЕ те, що
                залежить від стану підписки: іконка, заголовок і рядок під ним.
                Юзер 2026-05-22: сама картка виглядає ОДНАКОВО в усіх станах. */}
            <SubscriptionCard
              dense
              icon={isPaid && !isFrozen && !expiringSoon ? "crown" : isFrozen || expiringSoon ? "clock" : "bolt"}
              title={
                isPaid ? (
                  <>
                    Підписка{" "}
                    <span className="font-serif italic text-primary">
                      {subTier}
                    </span>
                  </>
                ) : (
                  <>
                    Відкрий{" "}
                    <span className="font-serif italic text-primary">
                      повний доступ
                    </span>
                  </>
                )
              }
              text={
                isFrozen
                  ? "Заморожена • розморозиться сама"
                  : expiringSoon
                    ? `Залишилось ${leftLabel} — продовжи зараз`
                    : isPaid
                      ? subEnds
                        ? `Активна ${subEnds} • ${leftLabel}`
                        : "Активна"
                      : "Миттєві вакансії + відкриті контакти"
              }
            />
          </div>
        );
      })()}

      {/* Ретеншн-офер (закреп із таймером) — тільки коли активний (юзер 2026-07-04) */}
      {/* Подарунок висить, поки людина його не забрала (owner 2026-09-13). */}
      <BirthdayGiftBanner wrapperClassName="px-5 pt-3" onOpen={openBirthdayGift} />
      <RetentionOfferBanner wrapperClassName="px-5 pt-3" />

      {subKnown && !isPremium && (
        <div className="px-5 pt-3">
          <WheelHeroCard />
        </div>
      )}

      {/* 3 рядки: Стрічка / Квести і бонуси / Реферали.
          База знань переїхала у блок «Навчання» до Курсу (owner 2026-08-06). */}
      <div className="px-5 pt-3">
        <div className="rounded-ios-card bg-surface shadow-card overflow-hidden">
          {mainRows.map((r, i) => (
            <MenuRowItem
              key={r.id}
              row={r}
              isLast={i === mainRows.length - 1}
              onLockedClick={setLockedSection}
            />
          ))}
        </div>
      </div>

      <div className="px-5 pt-3">
        <div className="rounded-ios-card bg-surface shadow-card overflow-hidden">
          {earnRows.map((r, i) => (
            <MenuRowItem
              key={r.id}
              row={r}
              isLast={i === earnRows.length - 1}
              onLockedClick={setLockedSection}
            />
          ))}
        </div>
      </div>

      {/* БЛОК 2: Навчання — Курс + База знань в одній картці.
          Owner 2026-08-06: inFLOW Intelligence вимкнено повністю, на його
          місце став розділ «База знань» (раніше жив у списку вище).
          Курс зверху (з прогрес-баром), тонкий розділювач, нижче — База знань. */}
      <div className="px-5 pt-3">
        <div className="rounded-ios-card bg-surface shadow-card overflow-hidden">
          <CourseRow
            grouped
            viewed={courseViewed}
            total={courseTotal}
            isPaid={isPremium}
            onLockedClick={() => setLockedSection("course")}
          />
          <MenuRowItem
            row={{
              id: "kb",
              title: "База знань",
              subtitle: "Гайди, подкасти та уроки від експертів",
              icon: "book",
              href: "/knowledge-base",
              locked: !isPremium,
              lockedKey: "knowledge_base",
            }}
            isLast
            onLockedClick={setLockedSection}
          />
        </div>
      </div>

      {/* INFLOW FINDER — розмістити вакансію (безкоштовно, для ВСІХ).
          Окремий бот @inflow_finderbot, де замовники шукають виконавців.
          Тап → попап-інструкція + кнопка переходу в бот. */}
      <div className="px-5 pt-3">
        <button
          type="button"
          onClick={() => {
            tg()?.HapticFeedback?.impactOccurred?.("light");
            showAppAlert(
              "Inflow Finder — безкоштовний бот, де замовники розміщують " +
                "вакансії.\n\n" +
                "Треба знайти виконавця? Просто розмісти вакансію — і " +
                "кандидати напишуть тобі.",
              {
                title: "Розмістити вакансію",
                emoji: "📝",
                buttons: [
                  {
                    label: "Розмістити вакансію",
                    variant: "primary",
                    // openTelegramLink мертвий на macOS → openTelegramChat
                    // (window.open), owner 2026-07-23.
                    onClick: () =>
                      openTelegramChat("https://t.me/inflow_finderbot"),
                  },
                  { label: "Закрити", variant: "ghost" },
                ],
              },
            );
          }}
          className="w-full relative flex items-center gap-3 overflow-hidden rounded-ios-card px-4 py-3.5 transition text-left bg-surface border-2 border-dashed border-primary/35 active:opacity-80"
        >
          {/* ⛔️ Тут був radial-glow. Знято (owner 2026-09-10: «яке убожество
              внизу з тінню зʼявилось»). Коло 7.5rem на картці ~62 px висотою
              закривало праву третину на всю висоту й читалось як брудна тінь —
              і чим ширший екран, тим більше (rem масштабується від ширини).
              Картку й так видно: пунктирна рамка + оранжева плитка. */}
          <div className="relative grid h-10 w-10 shrink-0 place-items-center rounded-[0.75rem] bg-primary text-white shadow-card-soft">
            <Icon name="doc" size={16} color="#FFFFFF" />
          </div>
          <div className="relative flex-1 min-w-0">
            <div className="font-display text-[0.90625rem] font-semibold text-ink leading-tight">
              Розмістити вакансію
            </div>
            <div className="text-[0.71875rem] text-ink-dim mt-0.5 leading-snug truncate">
              <span className="font-semibold text-primary">Безкоштовно</span>
              {" · "}знайди виконавця
            </div>
          </div>
          <Icon
            name="arrow-right"
            size={14}
            color="rgba(23,20,17,0.35)"
            className="relative shrink-0"
          />
        </button>
      </div>

      {/* MCP від inFLOW — для ChatGPT / Claude / Cursor (owner 2026-10-30).
          Окремою карткою під «Розмістити вакансію»: це не щоденний розділ, а
          разове налаштування, тож місце внизу; бейдж «new» тягне око, поки
          людина сторінку не відкрила. */}
      <div className="px-5 pt-3">
        <Link
          to="/mcp"
          className="relative flex items-center gap-3 rounded-ios-card bg-surface px-4 py-3.5 shadow-card transition active:bg-surface-soft"
        >
          {!mcpSeen && <CornerBadge>new</CornerBadge>}
          <div className="grid h-10 w-10 shrink-0 place-items-center rounded-[0.75rem] bg-primary/10 text-primary">
            <Icon name="ai" size={16} />
          </div>
          <div className="min-w-0 flex-1">
            <div className="font-display text-[0.90625rem] font-semibold leading-tight text-ink">
              {TITLES.mcp}
            </div>
            <div className="mt-0.5 truncate text-[0.71875rem] leading-snug text-ink-dim">
              Вакансії inFLOW у ChatGPT, Claude і Cursor
            </div>
          </div>
          <Icon name="arrow-right" size={14} color="rgba(23,20,17,0.35)" />
        </Link>
      </div>

      {/* ADMIN — DARK card, admin-only */}
      {me.is_admin && (
        <div className="px-5 pt-3">
          <Link
            to="/admin"
            className="flex items-center gap-3 rounded-ios-card bg-dark p-4 shadow-card active:bg-ink/85 transition"
          >
            <div className="grid h-10 w-10 place-items-center rounded-[0.75rem] bg-white/5 text-primary">
              <Icon name="shield" size={16} />
            </div>
            <div className="flex-1">
              <div className="font-display text-[0.875rem] font-semibold text-white">
                Адмін панель
              </div>
              <div className="mt-0.5 text-[0.6875rem] text-white/50">
                Тільки для адмінів
              </div>
            </div>
            <Icon
              name="arrow-right"
              size={14}
              color="rgba(255,255,255,0.5)"
            />
          </Link>
        </div>
      )}

      <LockedSectionPopup
        sectionKey={lockedSection}
        open={lockedSection !== null}
        onClose={() => setLockedSection(null)}
        isTest={isTest}
      />

      {supportOpen && <SupportPopup onClose={() => setSupportOpen(false)} />}
    </div>
  );
}

// ============================================================
// CourseRow — рядок Курсу з progress-баром «N/7 уроків»
// ============================================================

function CourseRow({
  viewed,
  total,
  isPaid,
  onLockedClick,
  grouped = false,
}: {
  viewed: number;
  total: number;
  isPaid?: boolean;
  onLockedClick?: () => void;
  /** grouped: рендеримось ВСЕРЕДИНІ спільної картки (Курс+База знань) —
   *  без власної рамки картки, з тонким розділювачем знизу. */
  grouped?: boolean;
}) {
  const locked = !isPaid;
  const pct = total > 0 ? Math.min(100, Math.round((viewed / total) * 100)) : 0;
  // У grouped-режимі прибираємо власну картку (її дає контейнер) і додаємо
  // тонкий розділювач знизу — як між рядками меню-блоку.
  const cardChrome = grouped ? "" : "rounded-ios-card bg-surface shadow-card ";
  const dividerStyle = grouped
    ? { borderBottom: "1px solid rgba(23,20,17,0.06)" }
    : undefined;

  // Locked: card без прогресу, з subtitle (стиль як інші menu rows) + popup
  if (locked) {
    return (
      <button
        type="button"
        onClick={onLockedClick}
        className={`w-full flex items-center gap-3 ${cardChrome}active:bg-surface-soft transition px-4 py-3.5 text-left`}
        style={dividerStyle}
      >
        {/* Locked — приглушений сірий значок + муатний заголовок (юзер 2026-06-25).
            Колір ОПАЧНИЙ, щоб перетини штрихів не темнішали. */}
        <div className="grid h-10 w-10 shrink-0 place-items-center rounded-[0.75rem] bg-ink/[0.05]">
          <Icon name="play" size={16} color="#A8A29C" />
        </div>
        <div className="flex-1 min-w-0">
          {/* Owner 2026-09-11: під замком опис був інший, ніж у підписників,
              і не згадував засновника. Тепер однаково в обох станах. */}
          <div className="font-display text-[0.90625rem] font-semibold text-ink-dim leading-tight">
            Курс із продажів
          </div>
          <div className="text-[0.71875rem] text-ink-faint mt-0.5 leading-snug truncate">
            Від засновника inFLOW
          </div>
        </div>
        <LockIcon />
      </button>
    );
  }

  return (
    <Link
      to="/course"
      className={`block ${cardChrome}active:bg-surface-soft transition px-4 py-3.5`}
      style={dividerStyle}
    >
      <div className="flex items-center gap-3">
        <div className="grid h-10 w-10 shrink-0 place-items-center rounded-[0.75rem] bg-primary/10 text-primary">
          <Icon name="play" size={16} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-display text-[0.90625rem] font-semibold text-ink">
            Курс із продажів
          </div>
          {/* Опис розділу — в один рядок (юзер 2026-06-14). */}
          <div className="mt-0.5 truncate text-[0.71875rem] text-ink-dim">
            Від засновника inFLOW
          </div>
        </div>
        <Icon name="arrow-right" size={14} color="rgba(23,20,17,0.35)" className="shrink-0 self-center" />
      </div>
      {/* Progress bar — трохи коротший, % у кінці (юзер 2026-06-14). */}
      <div className="mt-3 flex items-center gap-2.5">
        <div className="flex-1">
          <WavyProgressBar pct={pct} />
        </div>
        <span className="shrink-0 font-display text-[0.75rem] font-bold tabular-nums text-primary">
          {pct}%
        </span>
      </div>
    </Link>
  );
}

// ============================================================
// WavyProgressBar — чистий заокруглений прогрес-бар з делікатним
// «переливом» (sheen), що повільно проходить по заливці. Колишній
// хвилястий SVG-край прибрано (юзер 2026-06-14: «анімація — жах»).
// ============================================================

function WavyProgressBar({ pct }: { pct: number }) {
  const fill = Math.max(0, Math.min(100, pct));
  return (
    <div className="relative h-2 w-full overflow-hidden rounded-full bg-ink/[0.07]">
      <div
        className="absolute inset-y-0 left-0 overflow-hidden rounded-full transition-all duration-700 ease-out"
        style={{
          width: `${fill}%`,
          background:
            "linear-gradient(90deg, #FF8A3D 0%, #FE6B04 100%)",
        }}
      >
        {/* Делікатний світловий перелив, що ковзає по заливці */}
        {fill > 4 && (
          <span
            className="course-sheen absolute inset-y-0 left-0 w-1/3"
            style={{
              background:
                "linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.55) 50%, transparent 100%)",
            }}
          />
        )}
      </div>
    </div>
  );
}

// ============================================================
// MenuRowItem — рядок у списку розділів
// ============================================================

function MenuRowItem({
  row,
  isLast,
  onLockedClick,
}: {
  row: MenuRow;
  isLast?: boolean;
  onLockedClick?: (key: LockedSectionKey) => void;
}) {
  const style = {
    borderBottom: isLast ? undefined : "1px solid rgba(23,20,17,0.06)",
  };

  const content = (
    <>
      {/* Locked-розділ (немає підписки) — приглушуємо: сірий значок замість
          помаранчевого + муатний заголовок (юзер 2026-06-25). */}
      <div
        className={`grid h-10 w-10 shrink-0 place-items-center rounded-[0.75rem] ${
          row.locked ? "bg-ink/[0.05]" : "bg-primary/10 text-primary"
        }`}
      >
        {/* Locked — ОПАЧНИЙ сірий (не ink-faint): напівпрозорі штрихи в місцях
            перетину стакаються й виглядають чорними (юзер 2026-06-25). */}
        <Icon
          name={row.icon}
          // ЕТАЛОН — плитка розділу в профілі (owner 2026-09-11: «зараз мені
          // нравиться розмір і розташування в розділі профіль, зроби так
          // усюди»): плитка 40, рисунок 16 → зазор 12 px з усіх боків
          // (owner 2026-09-11, друга правка: «везде иконки на 2 уменьши»).
          size={16}
          color={row.locked ? "#A8A29C" : undefined}
        />
      </div>
      <div className="flex-1 min-w-0">
        <div
          className={`font-display text-[0.90625rem] font-semibold leading-tight ${
            row.locked ? "text-ink-dim" : "text-ink"
          }`}
        >
          {row.title}
        </div>
        <div
          className={`text-[0.71875rem] mt-0.5 leading-snug truncate ${
            row.locked ? "text-ink-faint" : "text-ink-dim"
          }`}
        >
          {row.subtitle}
        </div>
      </div>
      {row.meta && (
        <div className="font-mono text-[0.6875rem] font-bold text-primary bg-primary/10 px-2 py-0.5 rounded-full shrink-0">
          {row.meta}
        </div>
      )}
      {row.locked ? (
        <LockIcon />
      ) : (
        <Icon name="arrow-right" size={14} color="rgba(23,20,17,0.35)" />
      )}
    </>
  );

  if (row.locked && row.lockedKey && onLockedClick) {
    return (
      <button
        type="button"
        onClick={() => onLockedClick(row.lockedKey!)}
        className="w-full flex items-center gap-3 px-4 py-3.5 active:bg-surface-soft transition text-left"
        style={style}
      >
        {content}
      </button>
    );
  }

  // Рядок-дія (Підтримка) — кнопка, а не посилання.
  if (!row.href && row.onClick) {
    return (
      <button
        type="button"
        onClick={row.onClick}
        className="flex w-full items-center gap-3 px-4 py-3.5 text-left active:bg-surface-soft transition"
        style={style}
      >
        {content}
      </button>
    );
  }

  return (
    <Link
      to={row.locked || !row.href ? "/subscription" : row.href}
      className="flex items-center gap-3 px-4 py-3.5 active:bg-surface-soft transition"
      style={style}
    >
      {content}
    </Link>
  );
}

/** Замочок для locked-rows у FREE юзера. Єдиний канон padlock'а — 1:1 з
 *  Profile / Feed / Onboarding (юзер 2026-06-08: «скрізь однаковий замок»). */
function LockIcon() {
  return (
    <Icon name="lock" size={14} color="#A8A29C" />
  );
}
